/**
 * newAssignmentStore  – Step 2
 *
 * Local-only store for tracking technician alert state per work order.
 *
 * ── State model ──────────────────────────────────────────────────────────────
 *
 *  Each order ID progresses through three states (stored as separate Sets):
 *
 *   detected      – the order appeared in the technician's list for the first
 *                   time after baseline.  Persisted to localStorage so the
 *                   "New" marker survives a hard refresh or PWA reopen.
 *
 *   alert_shown   – the first toast/banner was displayed for this order.
 *                   Persisted to localStorage.  Gates the initial chime/
 *                   vibration (once per order, ever).
 *
 *   opened        – the technician tapped the order detail screen.
 *                   Persisted to localStorage.  Clears "New" badge and stops
 *                   reminders for this order.
 *
 *  Derived helpers:
 *   isNew(id)     → detected && !opened
 *   isAlerted(id) → alert_shown  (dedup gate in useNewAssignmentAlert)
 *
 * ── Reminder / cooldown ──────────────────────────────────────────────────────
 *
 *  reminderCooldownUntil – Map<orderId, timestamp> stored in memory only.
 *    After a reminder fires, the order is rate-limited for REMINDER_COOLDOWN_MS
 *    (90 s).  Prevents realtime + polling + visibilitychange from stacking.
 *
 *  pendingAlert  – ephemeral signal consumed by NewAssignmentBanner.
 *    kind: 'initial' | 'reminder' – lets the banner show different copy.
 *
 * ── Persistence ──────────────────────────────────────────────────────────────
 *  localStorage (not sessionStorage) so "New" badge survives reload / reopen.
 *  Cooldown map is session-only (in-memory).
 *
 * ── No backend changes in this step ─────────────────────────────────────────
 */

import { create } from 'zustand';

// ── Storage keys ──────────────────────────────────────────────────────────────
const DETECTED_KEY = 'fs_detected_order_ids';
const ALERTED_KEY  = 'fs_alerted_order_ids';
const OPENED_KEY   = 'fs_opened_order_ids';

// ── Reminder timing ───────────────────────────────────────────────────────────
/** Cooldown between reminders for the same order */
export const REMINDER_COOLDOWN_MS = 90_000; // 90 s
/** Delay before first reminder fires (if order unopened) */
export const REMINDER_DELAY_MS    = 60_000; // 60 s

// ── Persistence helpers ───────────────────────────────────────────────────────
function loadSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    const values = Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === 'string' && value.length > 0)
      : [];
    return new Set(values);
  } catch {
    return new Set();
  }
}

function saveSet(key: string, s: Set<string>): void {
  try {
    localStorage.setItem(key, JSON.stringify([...s]));
  } catch {
    // storage quota or private browsing – fail silently
  }
}

// ── Types ─────────────────────────────────────────────────────────────────────
export type AlertKind = 'initial' | 'reminder';

export interface PendingAlert {
  /** Unique id so React re-renders the banner even if count is unchanged */
  id: string;
  count: number;
  kind: AlertKind;
}

interface NewAssignmentState {
  // ── Persisted sets ──────────────────────────────────────────────────────────
  /** Orders that first appeared after the session baseline */
  detectedOrderIds: Set<string>;
  /** Orders for which the initial alert banner was shown */
  alertedOrderIds: Set<string>;
  /** Orders that have been opened by the technician */
  openedOrderIds: Set<string>;

  // ── Derived counter ─────────────────────────────────────────────────────────
  /** Number of orders with the active "New" badge (detected && !opened) */
  newCount: number;

  // ── Session-only ────────────────────────────────────────────────────────────
  /** Map<orderId, cooldownEndTimestamp> – prevents reminder spam */
  reminderCooldownUntil: Map<string, number>;
  /** Transient signal consumed by the banner; null = no active banner */
  pendingAlert: PendingAlert | null;

  // ── Actions ─────────────────────────────────────────────────────────────────
  /**
   * Called when realtime / polling detects newly assigned orders.
   * Fires a pendingAlert only for IDs not yet alerted.
   */
  addNew: (ids: string[]) => void;

  /**
   * Called by the reminder hook for orders that are still unopened.
   * Respects per-order cooldown.  Returns true if a reminder was queued.
   */
  fireReminder: (ids: string[]) => boolean;

  /** Called when the technician opens the order detail screen */
  markOpened: (id: string) => void;

  /**
   * 1.12.1 — Forget the given order ids: remove them from detected/alerted/opened
   * sets and persist. Used when an order transitions to a terminal status —
   * the "new assignment" signal is no longer meaningful, and persisting it
   * would cause the yellow border / push to linger on completed orders.
   */
  forgetIds: (ids: string[]) => void;

  /** Consumed by the banner component after it renders */
  clearPendingAlert: () => void;

  // ── Selectors ───────────────────────────────────────────────────────────────
  /** True while the New badge should be visible (detected && !opened) */
  isNew: (id: string) => boolean;
  /** True if the initial alert was already fired for this order */
  isAlerted: (id: string) => boolean;
  /** True if the order hasn't been opened yet */
  isUnopened: (id: string) => boolean;
  /** True if this order is still inside its reminder cooldown window */
  isReminderCoolingDown: (id: string) => boolean;
}

// ── Store ─────────────────────────────────────────────────────────────────────
export const useNewAssignmentStore = create<NewAssignmentState>()((set, get) => {
  const detectedOrderIds = loadSet(DETECTED_KEY);
  const alertedOrderIds  = loadSet(ALERTED_KEY);
  const openedOrderIds   = loadSet(OPENED_KEY);

  const initialNewCount = [...detectedOrderIds].filter(
    (id) => !openedOrderIds.has(id),
  ).length;

  return {
    detectedOrderIds,
    alertedOrderIds,
    openedOrderIds,
    newCount: initialNewCount,
    reminderCooldownUntil: new Map(),
    pendingAlert: null,

    addNew: (ids) => {
      if (ids.length === 0) return;

      set((state) => {
        const nextDetected = new Set(state.detectedOrderIds);
        const nextAlerted  = new Set(state.alertedOrderIds);
        const alertIds: string[] = [];

        for (const id of ids) {
          nextDetected.add(id);
          // Only fire initial alert once per order, and never for already-opened orders
          if (!nextAlerted.has(id) && !state.openedOrderIds.has(id)) {
            nextAlerted.add(id);
            alertIds.push(id);
          }
        }

        saveSet(DETECTED_KEY, nextDetected);

        if (alertIds.length === 0) {
          // Already alerted – persist detection silently, no banner
          return { detectedOrderIds: nextDetected };
        }

        saveSet(ALERTED_KEY, nextAlerted);

        const newCount = [...nextDetected].filter(
          (id) => !state.openedOrderIds.has(id),
        ).length;

        return {
          detectedOrderIds: nextDetected,
          alertedOrderIds: nextAlerted,
          newCount,
          pendingAlert: {
            id: crypto.randomUUID(),
            count: alertIds.length,
            kind: 'initial',
          },
        };
      });
    },

    fireReminder: (ids) => {
      if (ids.length === 0) return false;

      const now   = Date.now();
      const state = get();

      const eligible = ids.filter(
        (id) =>
          state.isNew(id) &&
          (state.reminderCooldownUntil.get(id) ?? 0) < now,
      );

      if (eligible.length === 0) return false;

      const nextCooldown = new Map(state.reminderCooldownUntil);
      for (const id of eligible) {
        nextCooldown.set(id, now + REMINDER_COOLDOWN_MS);
      }

      set({
        reminderCooldownUntil: nextCooldown,
        pendingAlert: {
          id: crypto.randomUUID(),
          count: eligible.length,
          kind: 'reminder',
        },
      });

      return true;
    },

    markOpened: (id) => {
      set((state) => {
        if (state.openedOrderIds.has(id)) return state;

        const nextOpened   = new Set(state.openedOrderIds);
        nextOpened.add(id);
        saveSet(OPENED_KEY, nextOpened);

        const newCount = [...state.detectedOrderIds].filter(
          (did) => !nextOpened.has(did),
        ).length;

        const nextCooldown = new Map(state.reminderCooldownUntil);
        nextCooldown.delete(id);

        return {
          openedOrderIds: nextOpened,
          newCount,
          reminderCooldownUntil: nextCooldown,
        };
      });
    },

    clearPendingAlert: () => set({ pendingAlert: null }),

    forgetIds: (ids) => {
      if (ids.length === 0) return;
      set((state) => {
        const nextDetected = new Set(state.detectedOrderIds);
        const nextAlerted = new Set(state.alertedOrderIds);
        const nextOpened = new Set(state.openedOrderIds);
        const nextCooldown = new Map(state.reminderCooldownUntil);
        let mutated = false;
        for (const id of ids) {
          if (nextDetected.delete(id)) mutated = true;
          if (nextAlerted.delete(id)) mutated = true;
          if (nextOpened.delete(id)) mutated = true;
          if (nextCooldown.delete(id)) mutated = true;
        }
        if (!mutated) return state;
        saveSet(DETECTED_KEY, nextDetected);
        saveSet(ALERTED_KEY, nextAlerted);
        saveSet(OPENED_KEY, nextOpened);
        const newCount = [...nextDetected].filter((did) => !nextOpened.has(did)).length;
        return {
          detectedOrderIds: nextDetected,
          alertedOrderIds: nextAlerted,
          openedOrderIds: nextOpened,
          reminderCooldownUntil: nextCooldown,
          newCount,
        };
      });
    },

    isNew:   (id) => {
      const s = get();
      return s.detectedOrderIds.has(id) && !s.openedOrderIds.has(id);
    },
    isAlerted: (id) => get().alertedOrderIds.has(id),
    isUnopened: (id) => !get().openedOrderIds.has(id),
    isReminderCoolingDown: (id) =>
      (get().reminderCooldownUntil.get(id) ?? 0) > Date.now(),
  };
});
